import { useEffect, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { toastError } from "@alpacahq/ui/old";

import { queryKeys } from "../queryKeys";
import { createWatchlist, type Watchlist } from "../rest/account";
import { getErrorMessage } from "./errorParsing";
import { useMutationRequest } from "./useMutationRequest";

export const PRIMARY_WATCHLIST_NAME = "Primary Watchlist";

// The combination of 422 and this error code is not unique across the entire API but it's
// unique enough to identify this particular error.
const DUPLICATE_WATCHLIST_NAME_CODE = 40010001;

const isDuplicateWatchlistNameError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  const data = error.response?.data as { code?: number } | undefined;
  return (
    error.response?.status === 422 &&
    data?.code === DUPLICATE_WATCHLIST_NAME_CODE
  );
};

/**
 * Idempotently ensures the account has a "Primary Watchlist".
 * Prevents duplicate POSTs when watchlists reference changes for any reason.
 */
export const useEnsurePrimaryWatchlist = ({
  accountID,
  watchlists,
  isFetched,
}: {
  accountID: string;
  watchlists: Watchlist[] | undefined;
  isFetched: boolean;
}) => {
  const queryClient = useQueryClient();

  // The account we've already attempted a create for. Prevents duplicate POSTs
  // when watchlists reference changes for any reason.
  const attemptedAccountRef = useRef<string | null>(null);

  const { mutate } = useMutationRequest(createWatchlist, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.watchlists.list(accountID),
      });
    },
    onError: (error) => {
      if (isDuplicateWatchlistNameError(error)) {
        // The watchlist already exists, so we can just invalidate the list query and return.
        queryClient.invalidateQueries({
          queryKey: queryKeys.watchlists.list(accountID),
        });
        return;
      }

      toastError(getErrorMessage(error), {
        toastId: "errorToastKey",
        autoClose: 3000,
        position: "top-right",
      });
    },
  });

  useEffect(() => {
    if (!accountID || !isFetched || !watchlists) {
      return;
    }
    if (watchlists.some((w) => w.name === PRIMARY_WATCHLIST_NAME)) {
      return;
    }
    if (attemptedAccountRef.current === accountID) {
      return;
    }
    attemptedAccountRef.current = accountID;
    mutate({ accountID, name: PRIMARY_WATCHLIST_NAME, symbols: [] });
  }, [accountID, isFetched, watchlists, mutate]);
};
